import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { type Dish } from "@fitness-app/data-models/entities/Dish";

import { backgroundTasksApi, supabase } from "~/store/initializeStore";

export type DishTaskSave = {
  taskId: string;
  stepId: string;
  changes: Partial<Dish>;
  dishId: string;
  issueId: string;
};

export const useDishTaskSave = () => {
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: async ({
      acceptedChanges,
      requestId,
    }: {
      acceptedChanges: Record<string, DishTaskSave[]>;
      requestId: string;
    }) => {
      const response = await backgroundTasksApi.post<{
        status: "success";
        data: {
          triggeredTasks: number;
          batchId: string;
          requestId: string;
        };
      }>("/api/trainers/ai/save-dish-issue-updates", {
        acceptedChanges,
        requestId,
      });

      return response.data;
    },
    onSuccess: (data) => {
      message.success(
        `Uruchomiono ${data.data.triggeredTasks} zadań w tle. Zmiany będą przetwarzane automatycznie i wkrótce zostaną zapisane.`,
      );
      queryClient.invalidateQueries({ queryKey: ["ai_agent_request"] });
      queryClient.invalidateQueries({ queryKey: ["dishIssues"] });
    },
    onError: () => {
      message.error("Błąd podczas zapisywania zmian");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ requestId }: { requestId: string }) => {
      // `.single()` — zero wierszy (RLS) ma być błędem, nie cichym 204 z zielonym toastem.
      await supabase
        .from("ai_agent_request")
        .update({ status: "DELETED", updatedAt: new Date().toISOString() })
        .eq("id", requestId)
        .select("id")
        .single()
        .throwOnError();
    },
    onSuccess: () => {
      message.success("Zadanie zostało pomyślnie usunięte");
      queryClient.invalidateQueries({ queryKey: ["ai_agent_request"] });
      queryClient.invalidateQueries({ queryKey: ["dishIssues"] });
    },
    onError: () => {
      message.error("Błąd podczas usuwania zadania");
    },
  });

  return {
    saveDishTaskMutation: saveMutation,
    deleteDishTaskMutation: deleteMutation,
  };
};
